<template>
  <div class="grid grid-rows-[1fr_auto] h-full w-full overflow-y-scroll">
    <div class="flex flex-col h-full gap-8 overflow-y-auto min-h-0 pb-6">
      <div class="flex flex-col gap-4 px-4">
        <!-- Customer Selection -->
        <div v-if="selectedCustomer?.smsOptedOut"
             class="p-3 rounded-lg bg-red-100 border border-red-300 font-medium text-sm mb-4 gap-3 flex text-red-400 items-center">
          <i class="pi pi-exclamation-circle text-red-600" />
          <span>הלקוח/ה ביקש/ה לא לקבל הודעות סמס, ולכן הודעות אישור ותזכורות לא יישלחו.</span>
        </div>
        <div v-if="!preDefinedCustomer" class="flex flex-col gap-2">
          <span class="font-medium">בחירת לקוח</span>
          <SelectCustomer
            v-model="customerId"
            @customer-selected="onCustomerSelected"
          />
        </div>

        <!-- Provider Selection -->
        <div v-if="shouldShowClientsChips" class="flex flex-col gap-2">
          <span class="font-medium">נותן השירות</span>
          <select-provider
            v-model="selectedProvider"
            @provider-selected="onProviderSelected"
          />
        </div>

        <!-- Service Selection -->
        <div class="flex flex-col gap-2">
          <span class="font-medium">שירותים</span>
          <select-service
            v-model="selectedServices"
            :selected-provider="selectedProvider"
            @services-selected="onServicesSelected"
          />
        </div>

        <!-- Date Selection -->
        <div class="flex flex-col gap-2">
          <span class="font-medium">{{ isRecurring ? 'תאריך התחלה' : 'תאריך ושעה' }}</span>
          <select-date
            :selected-hour="selectedHour"
            :selected-date="date"
            :selected-provider="selectedProvider"
            :pre-date="preDate"
            :selected-services="selectedServices"
            :selected-duration="durationOverride"
            @date-selected="onDateSelected"
            @hour-selected="onHourSelected"
            @duration-changed="onDurationChanged"
            @forced-hour-changed="onForcedHourChanged"
          />
        </div>

        <div class="border border-gray-200 rounded-lg">
          <settings-toggle
            :status="isRecurring"
            title="אירוע חוזר"
            :disabled="!date || selectedServices.length === 0"
            bg-color="white"
            @value-changed="(val) => {isRecurring = val}" />

          <!-- Frequency Selection (only when recurring) -->
          <div v-if="isRecurring" class="flex flex-col gap-2 px-4">
            <span class="font-medium">תדירות</span>
            <Select
              v-model="frequency"
              :options="frequencyOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="בחר תדירות"
              class="w-full" />
          </div>

          <!-- End Condition (only when recurring) -->
          <div v-if="isRecurring" class="flex flex-col gap-2 p-4">
            <span class="font-medium">סיום</span>
            <div class="flex gap-2 items-center justify-start">
              <RadioButton
                v-model="endCondition"
                name="endCondition"
                value="never" />
              <span>ללא סיום</span>
            </div>
            <div class="flex gap-2 items-center justify-start">
              <RadioButton
                v-model="endCondition"
                name="endCondition"
                value="date" />
              <span>עד תאריך</span>
              <button
                type="button"
                class="px-3 py-1.5 rounded-lg border border-gray-200 text-sm transition-colors"
                :class="endDate ? 'text-gray-800' : 'text-gray-400'"
                @click="openEndDatePicker"
              >{{ endDate ? DateTime.fromJSDate(endDate).toFormat('dd/MM/yyyy') : 'בחרו תאריך סיום' }}</button>
            </div>
          </div>

          <div v-if="isRecurring && conflicts.length > 0"
               class="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mx-4">
            <div class="flex items-start gap-2">
              <i class="pi pi-exclamation-triangle text-yellow-600 mt-1"></i>
              <div class="flex-1">
                <p class="font-medium text-yellow-800">נמצאו התנגשויות</p>
                <p class="text-sm text-yellow-700 mt-1">
                  {{ conflicts.length }} מתוך {{ totalEventsToCreate }} תורים מתנגשים עם תורים קיימים
                </p>
                <div v-if="conflicts.length <= 3" class="mt-2 space-y-1">
                  <div v-for="conflict in conflicts" :key="conflict.date" class="text-sm">
                    • {{ formatDate(conflict.date) }}
                  </div>
                </div>
                <p v-else class="text-sm mt-2">
                  מוצגים 3 ראשונים... (סה"כ {{ conflicts.length }})
                </p>
              </div>
            </div>
          </div>

          <!-- Preview -->
          <div v-if="recurringPreview" class="text-xs text-gray-600 bg-white p-2 rounded border border-gray-200 m-4">
            <i class="pi pi-info-circle ml-1" />
            {{ recurringPreview }}
          </div>
        </div>
      </div>
      <add-admin-note
        v-model="adminNote"
        @nodeAdded="onNoteAdded"
      />
    </div>
    <div class="bg-white shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.05)]">
      <drawer-footer
        class="mx-4"
        :approveDisable="!validateFilled"
        :approveLoading="loading || checkingConflicts"
        @approve-click="clickCTA"
        @cancel-click="closeDrawer"
      />
    </div>
  </div>

</template>

<script lang="ts" setup>
import {
  AppointmentType,
  ICustomer,
  IDropdownItem,
  IRecurringEventConflict,
  IService,
  RecurringEventFrequency
} from '@torim-app/types';

import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import { DateTime } from 'luxon';
import { getStringHour, totalServicesDuration } from '@torim-app/utils';
import { useAdminClientsStore, useAdminHomeStore, useAdminStore } from '@app/stores';
import { AppointmentsApi } from '@app/api/admin/appointments-api';
import { CustomersApi } from '@app/api/admin/customers-api';
import { RecurringEventsApi } from '@app/api/admin/recurring-events-api';
import Select from 'primevue/select';
import { getDayName } from '@app/utils/utils';
import SelectProvider from '@app/components/admin/components/SelectProvider.vue';
import SettingsDateSheet from '@app/components/admin/pages/configurations/ui/SettingsDateSheet.vue';
import { useDrawer } from '@torim-app/shared-components';
import { trackEvent } from '@app/composables/posthog';
import { useBookableProviders } from '@app/composables/bookable-providers';

const props = defineProps({
  clientId: {
    required: false,
    type: String,
    default: undefined
  },
  preDate: {
    required: false,
    type: Date,
    default: undefined
  },
  // Employee to start on — the one whose calendar slot was clicked. Ignored when the
  // user isn't allowed to book for other employees.
  preProviderId: {
    required: false,
    type: String,
    default: undefined
  }
});

const route = useRoute();
const adminStore = useAdminStore();
const adminClientsStore = useAdminClientsStore();
const adminHomeStore = useAdminHomeStore();
const toast = useToast();
const { openDrawer, closeDrawer } = useDrawer();

// "עד תאריך" opens the shared date drawer (same one used across scheduling/analytics) as a
// stacked drawer, instead of the inline date-picker popup.
const openEndDatePicker = () => {
  endCondition.value = 'date';
  openDrawer(SettingsDateSheet, {
    date: endDate.value instanceof Date && !isNaN(endDate.value.getTime()) ? endDate.value : undefined,
    subtitle: 'עד איזה תאריך לחזור על התור?',
    onPick: (picked: Date) => { endDate.value = picked; },
  }, { title: 'עד תאריך' });
};

const preDefinedCustomer = props.clientId ?? <string>route.params.id;
const customerId = ref<string>(preDefinedCustomer);
const selectedHour = ref<IDropdownItem>();
// Visit length (minutes) the owner set for this appointment instead of the services'
// own total. Undefined => the services decide, as before.
const durationOverride = ref<number | undefined>();
// True when the start time was typed by hand: book it exactly as entered, even over an
// existing visit or outside the working hours.
const forcedHour = ref(false);
const loading = ref(false);
// Start on the employee whose slot was clicked, as long as this user may book on that
// calendar — otherwise fall back to their own, so a booking never lands somewhere they
// can't even see.
const { bookableProviders, canBookFor } = useBookableProviders();
const selectedProvider = ref<string>(
  canBookFor(props.preProviderId) ? props.preProviderId! : adminStore.userId
);
const selectedServices = ref<IService[]>([]);
const adminNote = ref<string>();
const date = ref<Date>();
const selectedCustomer = ref<Pick<ICustomer, 'id' | 'fullName' | 'tags' | 'loyalty' | 'smsOptedOut' | 'phoneNumber'>>();
const today = new Date();

const isRecurring = ref(false);
const frequency = ref<RecurringEventFrequency>(RecurringEventFrequency.WEEKLY);
const endCondition = ref<'never' | 'date'>('never');
const endDate = ref<Date>();
const conflicts = ref<IRecurringEventConflict[]>([]);
const totalEventsToCreate = ref(0);
const generationLimit = ref<Date>();
const checkingConflicts = ref(false);

const frequencyOptions = computed(() => {
  if (!date.value) return [];

  const dayName = getDayName(date.value);
  return [
    { value: RecurringEventFrequency.DAILY, label: 'כל יום' },
    { value: RecurringEventFrequency.WEEKLY, label: `כל שבוע (${dayName})` },
    { value: RecurringEventFrequency.BI_WEEKLY, label: `כל שבועיים (${dayName})` },
    { value: RecurringEventFrequency.MONTHLY, label: `פעם בחודש (${dayName})` }
  ];
});

// Length of the visit being scheduled: the owner's override when they set one, else the
// services' own total (per-provider durations and steps included).
const visitDuration = computed<number>(() =>
  durationOverride.value ?? totalServicesDuration(selectedServices.value, selectedProvider.value)
);

const validateFilled = computed<boolean>(() => {
  return selectedServices.value.length > 0 && !!date.value && !!selectedHour.value && !!customerId.value && (isRecurring.value ? !checkingConflicts.value : true);
});

const isMainClient = computed(() => adminStore.getMainClient);
const services = computed<IService[]>(() =>
  adminStore.getServices.filter(service => service.providingClients.includes(selectedProvider.value))
);
// Shown to anyone with more than one calendar to choose from — managers, and employees
// granted their branch's calendar (they book for the colleagues they can already see).
const shouldShowClientsChips = computed(() => bookableProviders.value.length > 1);


const onCustomerSelected = (selectedCustomerObj: any) => {
  selectedCustomer.value = selectedCustomerObj;
  customerId.value = selectedCustomerObj.id;
};

const onNoteAdded = (note: string) => {
  adminNote.value = note;
};

const onProviderSelected = (provider: string) => {
  selectedProvider.value = provider;
};

const onServicesSelected = (services: IService[]) => {
  selectedServices.value = services;
};

const onDateSelected = (selectedDate: Date) => {
  date.value = selectedDate;
};

const onHourSelected = (hour: IDropdownItem) => {
  selectedHour.value = hour;
};

const onDurationChanged = (duration: number | undefined) => {
  durationOverride.value = duration;
};

const onForcedHourChanged = (forced: boolean) => {
  forcedHour.value = forced;
};

// When the drawer was opened by clicking a slot on the calendar (preDate carries
// the clicked time), pre-select that hour up front — as long as it's actually an
// available slot — so the owner doesn't have to open the time picker just to see
// it. Slots depend on the chosen services + provider, so this can only run once
// those are set. The matched IDropdownItem is passed down to the time drawer as
// initialHour, so opening it stays consistent.
let preselectingHour = false;
const maybePreselectHourFromPreDate = async () => {
  if (!props.preDate || selectedHour.value || preselectingHour) return;
  // Midnight means the drawer was opened on a day rather than on a slot (the "new
  // appointment" button) — there's no clicked time to restore, so leave the hour empty.
  if (props.preDate.getHours() === 0 && props.preDate.getMinutes() === 0) return;
  if (!date.value || !selectedProvider.value || selectedServices.value.length === 0) return;
  // Only meaningful while the chosen date is still the clicked day; a manual date
  // change should not force the originally-clicked hour.
  if (!DateTime.fromJSDate(date.value).hasSame(DateTime.fromJSDate(props.preDate), 'day')) return;

  preselectingHour = true;
  try {
    const serviceIds = selectedServices.value.map(s => s.id).join(',');
    const isoDate = DateTime.fromJSDate(date.value).toISODate()!;
    const slots = await CustomersApi.fetchSlotsByDateAndService(serviceIds, isoDate, selectedProvider.value, durationOverride.value);

    // The owner may have picked/changed things during the await — bail if stale.
    if (selectedHour.value || !props.preDate) return;

    const clickedDecimal = props.preDate.getHours() + props.preDate.getMinutes() / 60;
    const inList = (list: number[], v: number) => list.some(x => Math.abs(x - v) < 1e-6);
    const pick = (predicate: (v: number) => boolean): { value: number; admin: boolean } | undefined => {
      const a = slots.available.find(predicate);
      if (a !== undefined) return { value: a, admin: false };
      const b = slots.adminAvailable.find(predicate);
      if (b !== undefined) return { value: b, admin: true };
      return undefined;
    };

    // Prefer the exact clicked time; fall back to the first slot within the clicked
    // hour (mirrors the time drawer's own preDate matching).
    const matched = pick(v => Math.abs(v - clickedDecimal) < 1e-6)
      ?? pick(v => Math.floor(v) === props.preDate!.getHours());
    if (!matched) return;

    const stringHour = getStringHour(matched.value);
    selectedHour.value = {
      name: matched.admin ? `${stringHour} - admin` : stringHour,
      value: stringHour,
      numericValue: matched.value
    };
  } catch (e) {
    console.error('Failed to pre-select clicked slot:', e);
  } finally {
    preselectingHour = false;
  }
};

// Load predefined customer data if needed
onMounted(async () => {
  // Show the clicked day straight away. The hour has to wait for the services (slots
  // depend on them) and is filled in by maybePreselectHourFromPreDate.
  if (props.preDate && !date.value) date.value = props.preDate;

  if (preDefinedCustomer) {
    try {
      const customer = await CustomersApi.fetchCustomer(preDefinedCustomer);
      customerId.value = customer.id;
    } catch (error) {
      console.error('Failed to load predefined customer:', error);
    }
  }
});

const recurringPreview = computed(() => {
  if (!isRecurring.value || !date.value) return '';

  const bookingLimit = adminStore?.businessSiteData?.configurations?.allowToSchedule || 60;
  const generationLimit = DateTime.now().plus({ days: bookingLimit + 90 });

  let preview = 'יווצרו אירועים עד ';

  if (endCondition.value === 'never') {
    preview += generationLimit.toFormat('dd/MM/yyyy');
    preview += ' (מגבלת הזמנה + 90 יום)';
  } else if (endDate.value) {
    preview += DateTime.fromJSDate(endDate.value).toFormat('dd/MM/yyyy');
  }

  return preview;
});

const checkConflicts = async () => {
  if (!isRecurring.value || !date.value || !selectedHour.value?.numericValue) {
    conflicts.value = [];
    totalEventsToCreate.value = 0;
    return;
  }

  checkingConflicts.value = true;

  try {
    const servicesDuration = visitDuration.value;

    const response = await RecurringEventsApi.checkConflicts({
      type: AppointmentType.EVENT,
      timeFrom: selectedHour.value.numericValue,
      timeTo: selectedHour.value.numericValue + (servicesDuration / 60),
      providingClient: selectedProvider.value,
      frequency: frequency.value,
      startDate: DateTime.fromJSDate(date.value).toISO()!,
      endDate: endCondition.value === 'date' && endDate.value ?
        DateTime.fromJSDate(endDate.value).toISO()! : null,
      customer: customerId.value,
      requiredService: selectedServices.value.map(s => s.id),
      durationOverride: durationOverride.value
    });

    conflicts.value = response.conflicts || [];
    totalEventsToCreate.value = response.totalEventsToCreate || 0;
    generationLimit.value = response.generationLimit;
  } catch (error) {
    console.error('Failed to check conflicts:', error);
    toast.error('שגיאה בבדיקת התנגשויות');
  } finally {
    checkingConflicts.value = false;
  }
};


const formatDate = (dateStr: string | Date | undefined) => {
  if (!dateStr) return '';
  const dt = typeof dateStr === 'string' ? DateTime.fromISO(dateStr) : DateTime.fromJSDate(dateStr);
  return dt.setLocale('he').toFormat('dd/MM/yyyy');
};

const clickCTA = async (): Promise<void> => {
  trackEvent('appointment_created', { is_recurring: isRecurring.value });
  if (validateFilled.value && selectedHour.value?.numericValue && date.value) {
    loading.value = true;

    try {
      const servicesDuration = visitDuration.value;

      if (isRecurring.value) {
        const { createdCount, limitReached } = await RecurringEventsApi.createRecurringEvent({
          type: AppointmentType.EVENT,
          timeFrom: selectedHour.value.numericValue,
          timeTo: selectedHour.value.numericValue + (servicesDuration / 60),
          providingClient: selectedProvider.value,
          frequency: frequency.value,
          startDate: DateTime.fromJSDate(date.value).toISO()!,
          endDate: endCondition.value === 'date' && endDate.value ?
            DateTime.fromJSDate(endDate.value).toISO()! : null,
          customer: customerId.value,
          requiredService: selectedServices.value.map(s => s.id),
          adminNote: adminNote.value,
          durationOverride: durationOverride.value
        });

        // Report what the backend actually created — conflicting dates are booked too,
        // and the monthly plan limit can cut the series short.
        if (limitReached)
          toast.success(`נוצרו ${createdCount} תורים חוזרים - הגעתם למגבלת התורים החודשית`);
        else
          toast.success(`נוצרו ${createdCount} תורים חוזרים!`);
      } else {
        // Existing: Create single appointment
        await AppointmentsApi.addManualEvent({
          date: DateTime.fromJSDate(date.value, { zone: 'Asia/Jerusalem' }).toJSDate(),
          timeFrom: selectedHour.value.numericValue,
          timeTo: selectedHour.value.numericValue + (servicesDuration / 60),
          customerId: customerId.value,
          requiredService: selectedServices.value.map(x => x.id),
          providerId: selectedProvider.value,
          adminNote: adminNote.value,
          durationOverride: durationOverride.value,
          force: forcedHour.value
        });

        toast.success('התור נוצר!');
      }
    } catch (e: any) {
      console.log(e);
      if (e === 'Slot is taken')
        toast.error('נראה שהתור הזה נתפס');
      else if (e === 'Reached Limit')
        toast.error('הגעתם למגבלת התורים שלכם החודש, לא ניתן לזמן עוד תורים');
      else
        toast.error('לא הצלחנו ליצור את התור');
    } finally {
      loading.value = false;
      await adminHomeStore.fetchBusinessCalendarDataSilently();
      closeDrawer();
    }
  }
};

// Watchers
watch(() => selectedProvider.value, async () => {
  selectedServices.value.splice(0);
  date.value = undefined;
  selectedHour.value = undefined;
  // The override was chosen for a particular set of services on a particular employee.
  durationOverride.value = undefined;
  forcedHour.value = false;
}, { deep: true });

watch(() => date.value, async () => {
  if (isRecurring.value)
    await checkConflicts();

}, { deep: true });

watch(() => selectedServices.value, async () => {
  if (!date.value && props.preDate)
    date.value = props.preDate;

  await maybePreselectHourFromPreDate();

  if (isRecurring.value)
    await checkConflicts();
}, { deep: true });

// Also try once the date/provider are available (e.g. date set from preDate after
// services were chosen), so the clicked hour is pre-selected without opening the picker.
watch(() => [date.value, selectedProvider.value], async () => {
  await maybePreselectHourFromPreDate();
}, { deep: true });

// NEW: Watch recurring changes
watch(() => [isRecurring.value, frequency.value, endCondition.value, endDate.value, selectedHour.value],
  async () => {
    if (isRecurring.value && date.value && selectedHour.value) {
      await checkConflicts();
    }
  },
  { deep: true }
);

watch(() => isRecurring.value, (newVal) => {
  if (!newVal) {
    conflicts.value = [];
    totalEventsToCreate.value = 0;
    generationLimit.value = undefined;
  }
});

</script>

<style scoped>
.calendar {
  width: 100%;
}
</style>
